export const DEFAULT_CAMERA_ZOOM = 1;
export const MAX_FALLBACK_CAMERA_ZOOM = 8;
/**
 * Hardware zoom minimums from here up to just under 1 are a real wider lens
 * (Samsung 0.6×, Pixel 0.5×–0.7×). Lower minimums (Android's min 0) mean 1×.
 */
export const MIN_WIDE_CAMERA_ZOOM = 0.3;

export function normalizeCameraZoom(raw, fallback = DEFAULT_CAMERA_ZOOM) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(MAX_FALLBACK_CAMERA_ZOOM, Math.max(MIN_WIDE_CAMERA_ZOOM, n));
}

/** Widest zoom (e.g. 0.6) when the camera reaches its ultra-wide by zooming out. */
export function wideZoomMin(range) {
  const min = Number(range?.min);
  if (!Number.isFinite(min) || min < MIN_WIDE_CAMERA_ZOOM || min > 0.95) {
    return null;
  }
  return Math.round(min * 100) / 100;
}

export function readTrackZoomRange(track) {
  try {
    const caps = track?.getCapabilities?.() || {};
    const zoom = caps.zoom;
    if (zoom == null) return null;
    if (typeof zoom === "number") {
      if (!(zoom > 1)) return null;
      return { min: DEFAULT_CAMERA_ZOOM, max: zoom, step: 0.1 };
    }
    const min = Number(zoom.min);
    const max = Number(zoom.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
    return {
      min,
      max,
      step: Number(zoom.step) > 0 ? Number(zoom.step) : 0.01,
    };
  } catch {
    return null;
  }
}

export function clampCameraZoom(zoom, range) {
  const min = Number(range?.min);
  const max = Number(range?.max);
  const next = Number(zoom);
  const lo = Number.isFinite(min) ? min : DEFAULT_CAMERA_ZOOM;
  const hi = Number.isFinite(max)
    ? Math.min(max, MAX_FALLBACK_CAMERA_ZOOM)
    : MAX_FALLBACK_CAMERA_ZOOM;
  if (!Number.isFinite(next)) return lo;
  return Math.min(hi, Math.max(lo, next));
}

/** Native 1× for this track. Android often reports min 0 (not 1) as no zoom. */
export function identityTrackZoom(range) {
  if (wideZoomMin(range) != null) return DEFAULT_CAMERA_ZOOM;
  const min = Number(range?.min);
  if (Number.isFinite(min)) return min;
  return DEFAULT_CAMERA_ZOOM;
}

/**
 * Pinch / ± zoom stays 1× and up even when the hardware min is 0 (Android).
 * Going wider than 1× uses the wide lens chip (`wideZoomMin`).
 */
export function logicalZoomRange(range) {
  const max = Number(range?.max);
  const minHw = Number(range?.min);
  let logicalMax = MAX_FALLBACK_CAMERA_ZOOM;
  if (Number.isFinite(max)) {
    if (Number.isFinite(minHw) && minHw < 1 && wideZoomMin(range) == null) {
      logicalMax = Math.min(MAX_FALLBACK_CAMERA_ZOOM, 1 + (max - minHw));
    } else {
      logicalMax = Math.min(MAX_FALLBACK_CAMERA_ZOOM, max);
    }
  }
  return {
    min: DEFAULT_CAMERA_ZOOM,
    max: Math.max(DEFAULT_CAMERA_ZOOM, logicalMax),
  };
}

/**
 * Map UI zoom (1× and up) onto the track's hardware range.
 * A first-time back camera with min 0 must apply 0, not 1 (which already looks zoomed).
 */
export function toHardwareZoom(logicalZoom, range) {
  const logical = normalizeCameraZoom(logicalZoom);
  if (!range) return logical;
  const identity = identityTrackZoom(range);
  if (identity < 1) {
    return clampCameraZoom(identity + (Math.max(1, logical) - 1), range);
  }
  return clampCameraZoom(logical, range);
}

export async function applyTrackZoom(track, zoom) {
  const range = readTrackZoomRange(track);
  const logical = normalizeCameraZoom(zoom);
  if (!range || !track?.applyConstraints) {
    return {
      applied: false,
      hardware: false,
      zoom: logical,
      range: null,
    };
  }
  const next = toHardwareZoom(logical, range);
  try {
    await track.applyConstraints({ advanced: [{ zoom: next }] });
    return { applied: true, hardware: true, zoom: logical, range };
  } catch {
    try {
      await track.applyConstraints({ zoom: next });
      return { applied: true, hardware: true, zoom: logical, range };
    } catch {
      return { applied: false, hardware: false, zoom: logical, range };
    }
  }
}

export function pinchDistance(a, b) {
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export function nextPinchZoom(
  startZoom,
  startDistance,
  currentDistance,
  range
) {
  if (!(startDistance > 0) || !(currentDistance > 0)) {
    return clampCameraZoom(startZoom, range);
  }
  return clampCameraZoom(
    startZoom * (currentDistance / startDistance),
    range || { min: DEFAULT_CAMERA_ZOOM, max: MAX_FALLBACK_CAMERA_ZOOM }
  );
}

export function digitalCropSourceRect(videoWidth, videoHeight, zoom) {
  const vw = Math.max(1, Number(videoWidth) || 1);
  const vh = Math.max(1, Number(videoHeight) || 1);
  const z = Math.max(1, Number(zoom) || 1);
  const sw = vw / z;
  const sh = vh / z;
  return {
    sx: (vw - sw) / 2,
    sy: (vh - sh) / 2,
    sw,
    sh,
  };
}

export function formatCameraZoomLabel(zoom) {
  const n = normalizeCameraZoom(zoom);
  return `${n.toFixed(n >= 10 ? 0 : 1)}×`;
}
